import { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import Layout from "../Layout";
import ManagementLayout from "../../management/Layout";
import useFetchWithAuth from "../../../features/hooks/useFetchWithAuth";
import fetchWithAuth from "../../../util/fetchWithAuth";
import { API_URL } from "../../../util/url";
import { getRules } from "../../../util/rbac";

const emptyForm = { name: "", url: "", crm_api_key: "" };

function CrmsSupervisor({ variant = "supervisor" }) {
  const isManagementView = variant === "management";
  const PageLayout = isManagementView ? ManagementLayout : Layout;
  const { data, loading, error, refetch } = useFetchWithAuth("/statistic/crm/");
  const { data: meData } = useFetchWithAuth("/user/me/");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  const crms = useMemo(() => {
    if (Array.isArray(data)) return data;
    return Array.isArray(data?.results) ? data.results : [];
  }, [data]);

  const canManageCrm = useMemo(() => {
    const resource = isManagementView ? "management.crm" : "supervisor.crm";
    const rules = getRules(meData).filter((rule) => rule?.resource === resource);
    if (rules.some((rule) => String(rule?.effect).toUpperCase() === "DENY")) {
      return false;
    }
    return rules.some(
      (rule) =>
        String(rule?.effect).toUpperCase() === "ALLOW" &&
        String(rule?.access_level).toUpperCase() === "ADVANCED"
    );
  }, [isManagementView, meData]);

  const closeDialog = () => {
    if (saving) return;
    setOpen(false);
    setForm(emptyForm);
    setSaveError("");
  };

  const handleSave = async () => {
    const name = form.name.trim();
    const url = form.url.trim();
    if (!name || !url) {
      setSaveError("Вкажіть назву CRM і адресу сайту.");
      return;
    }

    setSaving(true);
    setSaveError("");
    try {
      const response = await fetchWithAuth(`${API_URL}/statistic/crm/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          url,
          crm_api_key: form.crm_api_key.trim(),
        }),
      });

      if (!response.ok) {
        let message = "Не вдалося додати CRM.";
        try {
          const payload = await response.json();
          message = payload.detail || payload.name?.[0] || payload.url?.[0] || message;
        } catch {
          // Keep the generic error when the server did not return JSON.
        }
        throw new Error(message);
      }

      setOpen(false);
      setForm(emptyForm);
      setSaveError("");
      await refetch();
    } catch (err) {
      setSaveError(err.message || "Не вдалося додати CRM.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageLayout>
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", sm: "center" }}
          gap={2}
          mb={3}
        >
          <Box>
            <Typography variant="h5" fontWeight={600}>
              CRM
            </Typography>
            <Typography color="text.secondary" mt={0.5}>
              Підключення CRM для операторів і скриптів
            </Typography>
          </Box>
          {canManageCrm && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpen(true)}
              sx={{ alignSelf: { xs: "stretch", sm: "auto" } }}
            >
              Додати CRM
            </Button>
          )}
        </Stack>

        {error && <Alert severity="error">Не вдалося завантажити список CRM.</Alert>}

        <TableContainer component={Paper} sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Назва</TableCell>
                <TableCell>Адреса</TableCell>
                <TableCell>Підключення</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={3} align="center">
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : crms.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} align="center">
                    CRM ще не додані
                  </TableCell>
                </TableRow>
              ) : (
                crms.map((crm) => (
                  <TableRow key={crm.id} hover>
                    <TableCell>{crm.name}</TableCell>
                    <TableCell>{crm.url || "-"}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={crm.crm_api_key ? "API-ключ" : "Ручне"}
                        color={crm.crm_api_key ? "success" : "default"}
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {canManageCrm && (
        <Dialog open={open} onClose={closeDialog} fullWidth maxWidth="sm">
          <DialogTitle>Додати CRM</DialogTitle>
          <DialogContent>
            <Stack gap={2} mt={1}>
              <TextField
                label="Назва CRM"
                value={form.name}
                onChange={(event) => setForm({ ...form, name: event.target.value })}
                required
                autoFocus
              />
              <TextField
                label="Адреса сайту"
                placeholder="https://bull-drop.com"
                value={form.url}
                onChange={(event) => setForm({ ...form, url: event.target.value })}
                required
              />
              <TextField
                label="API-ключ"
                value={form.crm_api_key}
                onChange={(event) =>
                  setForm({ ...form, crm_api_key: event.target.value })
                }
                helperText="Залиште порожнім, якщо CRM працює без API-ключа."
              />
              {saveError && <Alert severity="error">{saveError}</Alert>}
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={closeDialog} disabled={saving}>
              Скасувати
            </Button>
            <Button onClick={handleSave} variant="contained" disabled={saving}>
              {saving ? <CircularProgress size={22} color="inherit" /> : "Зберегти"}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </PageLayout>
  );
}

export default CrmsSupervisor;
